import React, { useState } from "react";
import styled from "styled-components";
import { Button, Panel, PanelProps, PanelStack2 } from "@blueprintjs/core";
import { faGithub, faGitlab } from "@fortawesome/free-brands-svg-icons";

import { IconButton, Input, InputValidation } from "@components";

import { SCMConfigUserInput, SCMTypeEnum } from "@pages/settings/types";

import "./ManageSCMPanelStyles.scss";

const DialogContainer = styled.div`
  padding: 24px;
  padding-bottom: 0px;
  width: 800px;
  background: white;
`;

const PanelRow = styled.div`
  margin: 12px 0;
`;

const PanelBox = styled.div`
  border: var(--global-purple-highlight) solid 1px;
  border-radius: 5px;
  padding: 10px;
  margin: 12px 0;
`;

const FlexDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChooseSCM = styled.div`
  padding: 60px;
`;

type SCMTypeChoicePanel = {};
type SCMFullConfigPanel = {
  scm_type: SCMTypeEnum;
  onFinish: (newSCM: SCMConfigUserInput) => void;
};

const SCMFullConfig: React.FC<PanelProps<SCMFullConfigPanel>> = ({
  scm_type,
  onFinish,
}) => {
  const [accessToken, setAccessToken] = useState<string>("");
  const [baseURL, setBaseURL] = useState<string>("");
  const [baseURLIsValid, setBaseURLIsValid] = useState<boolean>(false);
  const submit = () => {
    onFinish({
      scm_type: scm_type,
      access_token: accessToken,
      base_url: baseURL,
    });
  };

  const baseUrlValidators = [
    {
      description: "Hostname cannot be github.com or gitlab.com",
      isValid: (enteredUrl: string) => {
        try {
          const url = new URL(enteredUrl);
          return url.hostname !== "github.com" && url.hostname !== "gitlab.com";
        } catch (_) {
          return false;
        }
      },
    },
    {
      description: "Cannot end with /",
      isValid: (enteredUrl: string) => !enteredUrl.endsWith("/"),
    },
  ];

  return (
    <>
      <PanelRow>
        An Access Token is required to give semgrep-app permission to post{" "}
        {scm_type === "github_enterprise" ? "pull request" : "merge request"}{" "}
        comments. Please create an Access Token on your{" "}
        {scm_type === "github_enterprise"
          ? "Github Enterprise"
          : "GitLab Self-Managed"}{" "}
        server and insert below.
      </PanelRow>
      <PanelBox className={"dialogBody"} style={{ margin: "12px 0" }}>
        <PanelRow>
          <h4> Access Token </h4>
          <FlexDiv>
            {/* Protect sensitive data by masking in fullstory recordings
                https://help.fullstory.com/hc/en-us/articles/360020623574 */}
            <form className="fs-mask dd-privacy-mask">
              <Input
                placeholder="required"
                type="password"
                onChange={(value) => setAccessToken(value.target.value)}
                style={{ width: "520px" }}
              />
            </form>
          </FlexDiv>
        </PanelRow>
        <PanelRow>
          <h4> SCM Base URL </h4>
          <Input
            placeholder="https://gitlab.example.com"
            type="url"
            onChange={(value) => setBaseURL(value.target.value)}
            style={{ width: "520px" }}
          />
          <InputValidation
            value={baseURL}
            validators={baseUrlValidators}
            setValidInput={setBaseURLIsValid}
          />
        </PanelRow>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            disabled={!accessToken || !baseURL || !baseURLIsValid}
            onClick={submit}
            intent="primary"
          >
            Create
          </Button>
        </div>
      </PanelBox>
    </>
  );
};

interface ManageSCMPanelProps {
  onFinish: (newSCM: SCMConfigUserInput) => void;
}

export const ManageSCMPanel: React.FC<ManageSCMPanelProps> = ({ onFinish }) => {
  const scmTypeChoice: React.FC<PanelProps<SCMTypeChoicePanel>> = ({
    openPanel,
  }) => {
    const openGitHubEnterprise = () =>
      openPanel({
        props: {
          scm_type: "github_enterprise",
          onFinish: onFinish,
        },
        renderPanel: SCMFullConfig,
        title: "Configure Github Enterprise",
      });
    const openGitLabSelfManaged = () =>
      openPanel({
        props: {
          scm_type: "gitlab_selfmanaged",
          onFinish: onFinish,
        },
        renderPanel: SCMFullConfig,
        title: "Configure GitLab Self-Managed",
      });

    return (
      <div style={{ height: 200 }}>
        <ChooseSCM>
          <IconButton
            title="GitHub Enterprise"
            icon={faGithub}
            onClick={openGitHubEnterprise}
          />
          <IconButton
            title="GitLab Self-Managed"
            icon={faGitlab}
            onClick={openGitLabSelfManaged}
          />
        </ChooseSCM>
      </div>
    );
  };

  const initialPanel: Panel<SCMTypeChoicePanel> = {
    props: {},
    renderPanel: scmTypeChoice,
    title: "Select SCM",
  };
  const [currentPanelStack, setCurrentPanelStack] = React.useState<
    Array<Panel<SCMTypeChoicePanel | SCMFullConfigPanel>>
  >([initialPanel]);

  const addToPanelStack = React.useCallback(
    (newPanel: Panel<SCMTypeChoicePanel | SCMFullConfigPanel>) =>
      setCurrentPanelStack((stack) => [...stack, newPanel]),
    []
  );
  const removeFromPanelStack = React.useCallback(
    () => setCurrentPanelStack((stack) => stack.slice(0, -1)),
    []
  );
  return (
    <DialogContainer>
      <div>
        <PanelStack2
          className="panel-stack-scm"
          onOpen={addToPanelStack}
          onClose={removeFromPanelStack}
          renderActivePanelOnly={true}
          showPanelHeader={true}
          stack={currentPanelStack}
        />
      </div>
    </DialogContainer>
  );
};
